import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Tooltip,
  Chip,
  Avatar,
  Card,
  CardContent,
  useTheme,
  alpha,
  Alert,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Add as AddIcon,
  LocalShipping as ParcelIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import FilterBar from '../../common/FilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { PARCEL_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canModifyParcel, canArchiveParcel } from '../../../utils/parcelPermissions';
import { compareParcelListItems, getParcelStatusSortRank, resolveParcelStatus } from '../../../utils/parcelStatus';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { SUCCESS, glassCardSx } from '../../common/SaaSTokens';

const normalizeUsers = (res) => {
  const data = res?.data;
  const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  return list
    .filter((u) => u?.id != null)
    .map((u) => ({ value: String(u.id), label: u.name || `User #${u.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const ParcelList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();

  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [users, setUsers] = useState([]);

  const MotionCard = motion(Card);
  const cardShellSx = glassCardSx;

  const canCreateParcel = Boolean(currentUser);
  const canEditParcel = (parcel) => canModifyParcel(currentUser, parcel);
  const canArchiveParcelRow = (parcel) => canArchiveParcel(currentUser, parcel);

  const userFilterOptions = useMemo(() => users, [users]);

  const filterOptions = useMemo(
    () => [
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { value: 'pending', label: 'Pending' },
          { value: 'arrived', label: 'Arrived' },
          { value: 'collected', label: 'Collected' },
          { value: 'returned', label: 'Returned' },
          { value: 'archived', label: 'Archived' },
        ],
      },
      {
        name: 'parcel_type',
        label: 'Type',
        type: 'select',
        options: [
          { value: 'document', label: 'Document' },
          { value: 'package', label: 'Package' },
          { value: 'equipment', label: 'Equipment' },
          { value: 'food', label: 'Food Items' },
          { value: 'electronics', label: 'Electronics' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        name: 'buyer_id',
        label: 'Sender',
        type: 'select',
        searchable: true,
        options: userFilterOptions,
      },
      {
        name: 'receiver_id',
        label: 'Receiver',
        type: 'select',
        searchable: true,
        options: userFilterOptions,
      },
    ],
    [userFilterOptions]
  );

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchParcels({ forceRefresh: Boolean(location.state?.refreshList) });
    const fetchUsers = async () => {
      try {
        const res = await api.get('/users', { params: { minimal: 1, active_only: 1 } });
        setUsers(normalizeUsers(res));
      } catch {
        setUsers([]);
      }
    };
    fetchUsers();
  }, [currentUser, location.pathname, location.state?.refreshList]);

  const fetchParcels = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/parcels', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setParcels(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching parcels:', error);
      if (error.response?.status === 403 && error.response?.data?.message !== 'Unauthorized') {
        navigate('/dashboard');
      } else {
        setError('Failed to load parcels. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = (parcel) => {
    setSelectedParcel(parcel);
    setArchiveDialogOpen(true);
  };

  const confirmArchive = async () => {
    if (!selectedParcel) return;
    try {
      setArchiveLoading(true);
      await api.post(`/parcels/${selectedParcel.id}/archive`);
      setParcels((prev) => prev.filter((p) => p.id !== selectedParcel.id));
      setArchiveDialogOpen(false);
      setSelectedParcel(null);
      setSuccess(`Parcel "${selectedParcel.tracking_number}" has been archived.`);
      await fetchParcels({ forceRefresh: true });
    } catch (err) {
      console.error('Error archiving parcel:', err);
      setError(err?.response?.data?.message || 'Failed to archive parcel. Please try again.');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleRestore = async (parcel) => {
    try {
      await api.post(`/parcels/${parcel.id}/restore`);
      setParcels((prev) => prev.filter((item) => item.id !== parcel.id));
      setSuccess(`Parcel "${parcel.tracking_number}" has been restored.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to restore parcel. Please try again.');
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    setFilters(newFilters);
    setPage(0);
    if (archivedViewChanged) {
      clearSelection();
      fetchParcels({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({});
    setSearchTerm('');
    setActiveStatFilter('all');
    setPage(0);
    if (wasArchived) fetchParcels({ forceRefresh: true });
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'pending', 'arrived', 'collected'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);
    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'arrived': return 'info';
      case 'collected': return 'success';
      case 'returned': return 'error';
      case 'archived': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <AccessTimeIcon />;
      case 'arrived': return <InventoryIcon />;
      case 'collected': return <CheckCircleIcon />;
      case 'archived': return <ArchiveIcon />;
      default: return undefined;
    }
  };

  const getParcelInitials = (trackingNumber) => {
    if (!trackingNumber || typeof trackingNumber !== 'string') return 'PR';
    return trackingNumber
      .split('')
      .filter(char => /[A-Za-z0-9]/.test(char))
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const filteredParcels = parcels
    .filter((parcel) => {
      const matchesSearch =
        (parcel.tracking_number?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (parcel.description?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (parcel.receiver?.name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
        (parcel.buyer?.name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase());

      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        if (key === 'status') return resolveParcelStatus(parcel) === value;
        if (key === 'parcel_type') return parcel.parcel_type === value;
        if (key === 'buyer_id') return String(parcel.buyer_id ?? parcel.buyer?.id) === String(value);
        if (key === 'receiver_id') return String(parcel.receiver_id ?? parcel.receiver?.id) === String(value);
        return true;
      });

      return matchesSearch && matchesFilters;
    })
    .sort(compareParcelListItems);

  const statsData = [
    { icon: ParcelIcon, value: parcels.length, label: 'Total Parcels', color: 'info' },
    { icon: AccessTimeIcon, value: parcels.filter(p => !p.archived_at && p.status === 'pending').length, label: 'Pending', color: 'warning' },
    { icon: InventoryIcon, value: parcels.filter(p => !p.archived_at && p.status === 'arrived').length, label: 'Arrived', color: 'primary' },
    { icon: CheckCircleIcon, value: parcels.filter(p => !p.archived_at && p.status === 'collected').length, label: 'Collected', color: 'success' },
  ];

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              Please log in to view this page.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading parcels list" size={56} variant="primary" />
    </Box>
  );

  const columns = [
    {
      key: 'tracking_number',
      label: 'Parcel',
      icon: ParcelIcon,
      minWidth: 220,
      render: (parcel) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
            {getParcelInitials(parcel.tracking_number)}
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{parcel.tracking_number}</Typography>
        </Box>
      )
    },
    {
      key: 'receiver_name',
      label: 'Recipient',
      icon: PersonIcon,
      minWidth: 160,
      sortAccessor: (parcel) => parcel.receiver?.name || '',
      render: (parcel) => parcel.receiver?.name || '-'
    },
    {
      key: 'buyer_name',
      label: 'Sender',
      icon: PersonIcon,
      minWidth: 160,
      sortAccessor: (parcel) => parcel.buyer?.name || '',
      render: (parcel) => parcel.buyer?.name || '-'
    },
    {
      key: 'received_date',
      label: 'Collected Date',
      icon: ScheduleIcon,
      minWidth: 140,
      sortAccessor: (parcel) => (parcel.status === 'collected' && parcel.received_date ? new Date(parcel.received_date).getTime() : 0),
      render: (parcel) => (
        parcel.status === 'collected' && parcel.received_date
          ? format(new Date(parcel.received_date), 'MMM dd, yyyy')
          : '-'
      ),
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 140,
      sortAccessor: (parcel) => getParcelStatusSortRank(parcel),
      render: (parcel) => {
        const actualStatus = resolveParcelStatus(parcel);
        return (
          <Chip
            icon={getStatusIcon(actualStatus)}
            label={actualStatus.charAt(0).toUpperCase() + actualStatus.slice(1)}
            color={getStatusColor(actualStatus)}
            size="small"
            sx={actualStatus === 'archived' ? {
              color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#475569',
              backgroundColor: alpha('#94A3B8', theme.palette.mode === 'dark' ? 0.2 : 0.14),
              border: `1px solid ${alpha('#94A3B8', 0.45)}`,
              fontWeight: 700,
              '& .MuiChip-icon': {
                color: theme.palette.mode === 'dark' ? '#CBD5E1' : '#64748B',
              },
            } : undefined}
          />
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      minWidth: 160,
      render: (parcel) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Parcel">
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); navigate(`/parcels/${parcel.id}`); }}
              sx={{ color: theme.palette.info.main, '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.1) } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEditParcel(parcel) && !parcel.archived_at && (
            <Tooltip title="Edit Parcel">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); navigate(`/parcels/edit/${parcel.id}`); }}
                sx={{ color: theme.palette.warning.main, '&:hover': { backgroundColor: alpha(theme.palette.warning.main, 0.1) } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canModifyParcel(currentUser, parcel) && parcel.archived_at && (
            <Tooltip title="Restore Parcel">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleRestore(parcel); }}
                sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) } }}
              >
                <RestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canArchiveParcelRow(parcel) && (
            <Tooltip title="Archive Parcel">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); handleArchive(parcel); }}
                sx={{ color: theme.palette.error.main, '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) } }}
              >
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Header
        title="Parcel Management"
        subtitle="Track and manage incoming and outgoing parcels"
        icon={ParcelIcon}
        actionButton={
          canCreateParcel ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/parcels/add')}
              sx={{
                bgcolor: 'rgba(255,255,255,0.22)',
                backdropFilter: 'blur(10px)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.34)' },
                borderRadius: 2,
                px: 2.5,
                py: 1.5,
              }}
            >
              Add Parcel
            </Button>
          ) : null
        }
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />

      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={
          activeStatFilter === 'pending' ? 1
          : activeStatFilter === 'arrived' ? 2
          : activeStatFilter === 'collected' ? 3
          : 0
        }
      />

      <MotionCard
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.16 }}
        elevation={0}
        sx={{ ...cardShellSx, mb: 2.5 }}
      >
        <CardContent sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
          <FilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            filterOptions={filterOptions}
            searchPlaceholder="Search by tracking number, description, recipient, or sender..."
          />
        </CardContent>
      </MotionCard>

      {filters.status !== 'archived' && <ListBulkToolbar
        config={PARCEL_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        onSuccess={createBulkHandlers({
          endpoint: PARCEL_BULK_CONFIG.endpoint,
          setItems: setParcels,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'parcel(s)',
          refetch: fetchParcels,
          setError,
        }).onSuccess}
        onDeleteSuccess={async () => {
          if (!selectedIds?.length) {
            setError('No selected parcels can be archived.');
            return;
          }

          try {
            await Promise.all(selectedIds.map((parcelId) => (
              api.post(`/parcels/${Number(parcelId)}/archive`)
            )));

            const selectedIdSet = new Set(selectedIds.map(String));
            setParcels((prev) => (
              Array.isArray(prev)
                ? prev.filter((row) => !selectedIdSet.has(String(row.id)))
                : prev
            ));

            setError(null);
            setSuccess(`${selectedIds.length} parcel(s) archived successfully.`);
            clearSelection();
            fetchParcels({ forceRefresh: true });
          } catch (err) {
            setError(err?.response?.data?.message || 'Failed to archive selected parcels.');
          }
        }}
      />}

      <TableList
        columns={columns}
        data={filteredParcels}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...(filters.status !== 'archived'
          ? bulkTableProps(PARCEL_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)
          : {})}
        loading={loading}
        emptyState={{
          icon: <ParcelIcon />,
          title: 'No parcels found',
          description: searchTerm || Object.keys(filters).length > 0
            ? 'Try adjusting your search or filters.'
            : 'Start by adding your first parcel.',
        }}
        onRowClick={(parcel) => navigate(`/parcels/${parcel.id}`)}
      />

      <ConfirmDeleteDialog
        open={archiveDialogOpen}
        onClose={() => { setArchiveDialogOpen(false); setSelectedParcel(null); }}
        onConfirm={confirmArchive}
        loading={archiveLoading}
        title="Archive Parcel"
        itemType="parcel"
        itemName={selectedParcel?.tracking_number}
        description="The parcel will be archived and remain in the system for history and audit purposes."
        confirmLabel="Archive Parcel"
        actionVerb="archive"
      />
    </Container>
  );
};

export default ParcelList;
